import { useCallback } from "react";
import { useSelector } from "react-redux";
import {
  fetchFeatureUsageInfo,
  isUsageLimitError,
  parseUsageLimitError,
} from "../utils/usageApi";
import {
  showUsageNotification,
  showUsageLimitExceededNotification,
} from "../components/site/UsageNotification/UsageNotification";

/**
 * Custom hook for managing usage notifications
 * @param {string} featureType - Feature type (product_analytics or brand_analytics)
 * @returns {Object} Hook functions
 */
export const useUsageNotification = (featureType) => {
  const {
    userProfile: { data: userProfile },
  } = useSelector((state) => state.user);

  /**
   * Fetch and display usage notification
   */
  const showUsageInfo = useCallback(async () => {
    try {
      const usageData = await fetchFeatureUsageInfo(featureType);

      // Only show if limits are enforced
      if (usageData.is_limited) {
        // Create full usage data object in the format expected by showUsageNotification
        const formattedData = {
          is_limited: usageData.is_limited,
          tariff_name: usageData.feature,
          usage: {
            [featureType]: {
              current_usage: usageData.current_usage,
              limit: usageData.limit,
              remaining: usageData.remaining,
              percentage_used: usageData.percentage_used,
              is_unlimited: usageData.is_unlimited,
            },
          },
        };

        showUsageNotification(formattedData, featureType, userProfile);
      }
    } catch (error) {
      console.error("Error fetching usage info:", error);
      // Don't show error to user, just log it
    }
  }, [featureType, userProfile]);

  /**
   * Handle API errors and show appropriate notification if it's a usage limit error
   * @param {Object} error - Error object from API
   * @returns {boolean} True if it was a usage limit error
   */
  const handleApiError = useCallback((error) => {
    if (error && error.detail && isUsageLimitError(error)) {
      const errorDetails = parseUsageLimitError(error);
      showUsageLimitExceededNotification(errorDetails);
      return true;
    }
    return false;
  }, []);

  return {
    showUsageInfo,
    handleApiError,
  };
};

export default useUsageNotification;
